import { Modal, Button, Form } from "react-bootstrap";
import { useEffect, useState } from "react";

import TestStripForm from "./TestStripForm";
import FTIRForm from "./FTIRForm";
import LabResultForm from "./LabResultForm";

import { v4 as uuidv4 } from "uuid";

const isValidQuantification = (value) => {
  if (value.trim() === "") return true;
  return /^\d+(\.\d*)?$/.test(value.trim());
};

const OtherResultModal = ({
  show,
  handleClose,
  handleSave,
  resultData,
  isNewResult,
  newResultType,
}) => {
  const [isResultData, setIsResultData] = useState(false);
  const [formData, setFormData] = useState({
    testType: "",
  });
  const [validated, setValidated] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);

  useEffect(() => {
    if (resultData) {
      setFormData(resultData);
      setIsResultData(true);
    } else if (newResultType) {
      setFormData({ testType: newResultType, ftirResults: [] });
    }
  }, [resultData, newResultType]);

  const handleChange = (id, value) => {
    setFormData((prevFormData) => {
      const updated = { ...prevFormData, [id]: value };

      if (id === "testType") {
        return {
          ...updated,
          results: "",
          ftirResults: [],
          type: "",
          otherTechniqueType: "",
          comments: "",
        };
      }

      return updated;
    });
  };

  const resetStates = () => {
    setFormData({}); // Clear form data;
    setValidated(false);
    setShowValidationError(false);
    setIsResultData(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    const hasInvalidFTIRQuantification =
      formData.testType === "FTIR" &&
      Array.isArray(formData.ftirResults) &&
      formData.ftirResults.length > 0 &&
      formData.ftirResults.some(
        (row) =>
          row.quantification &&
          row.quantification.trim() !== "" &&
          !isValidQuantification(row.quantification),
      );

    const hasMissingSubstanceInFTIR =
      formData.testType === "FTIR" &&
      Array.isArray(formData.ftirResults) &&
      formData.ftirResults.length > 0 &&
      formData.ftirResults.some(
        (row) =>
          row &&
          row.quantification &&
          row.quantification.trim() !== "" &&
          (!row.substance || row.substance.trim() === ""),
      );

    const hasMissingTestType =
      ((formData.testType === "testStrip" ||
        formData.testType === "otherTechnique") &&
        !formData.type);

    const isValid =
      form.checkValidity() === true &&
      formData.testType &&
      formData.results &&
      !hasMissingTestType &&
      !hasInvalidFTIRQuantification &&
      !hasMissingSubstanceInFTIR;

    if (!isValid) {
      event.stopPropagation();
      setShowValidationError(true);
    } else {
      let normalizedFormData = { ...formData };

      // Normalize trailing decimals in FTIR quantification values
      if (
        normalizedFormData.testType === "FTIR" &&
        Array.isArray(normalizedFormData.ftirResults)
      ) {
        const normalizedFtirResults = normalizedFormData.ftirResults.map(
          (row) => {
            const q = String(row?.quantification ?? "").trim();
            return {
              ...row,
              quantification: q.endsWith(".") ? q + "0" : q,
            };
          },
        );

        // Regenerate results string with normalized values
        const normalizedResults = normalizedFtirResults
          .filter((row) => {
            const substance = String(row?.substance ?? "").trim();
            const quantification = String(row?.quantification ?? "").trim();
            return substance !== "" || quantification !== "";
          })
          .map((row) => {
            const substance = String(row?.substance ?? "").trim();
            const quantification = String(row?.quantification ?? "").trim();

            if (substance && quantification)
              return `${substance} (${quantification}%)`;
            if (substance) return substance;
            return `${quantification}%`;
          })
          .join(", ");

        normalizedFormData = {
          ...normalizedFormData,
          ftirResults: normalizedFtirResults,
          results: normalizedResults,
        };
      }

      normalizedFormData = {
        ...normalizedFormData,
        resultId: normalizedFormData.resultId || uuidv4(),
        ...(isNewResult
          ? { addedDt: new Date().toISOString() }
          : { editedDt: new Date().toISOString() }),
      };

      handleSave(normalizedFormData);
      resetStates();
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        handleClose();
        resetStates();
      }}
      backdrop='static'
      size='lg'
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {isResultData
            ? "Edit Results"
            : newResultType === "testStrip"
              ? "Enter Test Strip Results"
              : newResultType === "FTIR"
                ? "Enter FTIR Results"
                : "Enter Laboratory Results"}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Body>
          {formData.testType === "testStrip" && (
            <TestStripForm
              formData={formData}
              handleChange={handleChange}
              showValidationError={showValidationError}
            />
          )}
          {formData.testType === "FTIR" && (
            <FTIRForm
              formData={formData}
              handleChange={handleChange}
              showValidationError={showValidationError}
            />
          )}
          {formData.testType === "otherTechnique" && (
            <LabResultForm
              formData={formData}
              handleChange={handleChange}
              showValidationError={showValidationError}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='secondary'
            onClick={() => {
              handleClose();
              resetStates();
            }}
          >
            Cancel
          </Button>
          <Button variant='primary' type='submit'>
            Save
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default OtherResultModal;
